import { c as _c } from "react/compiler-runtime";
import { isScanSection } from "@utils/markup-location-utils";
import { isIElementPointCloudStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useAppSelector } from "@store/store-helper";
import { incompleteMarkupAttachmentUploadTasksSelector } from "@store/upload-tasks/upload-tasks-selector";
import { isTaskInProgress } from "@hooks/upload-tasks/upload-tasks-utils";
import { SidePanel } from "@components/sidepanel/sidepanel";
import { StickySidePanelContainer } from "@components/sidepanel/sticky-sidepanel-container";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { Skeleton } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui-wrapper";
import { MarkupsSidePanelAttachments, MarkupsSidePanelHeader, MarkupsSidePanelInfo } from "@pages/project-details/project-markups/sidepanel";
import { MarkupPreview } from "@pages/project-details/project-markups/sidepanel/preview/markup-preview";
import { MarkupSidePanelResizeHandle } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-resize-handle";
import { getMarkupPreviewPlaceId, useMarkupPreview } from "@pages/project-details/project-markups/sidepanel/preview/use-markup-preview";
import { useMarkupSidePanel } from "@pages/project-details/project-markups/sidepanel/use-markup-sidepanel";
import { PALETTE_VARS } from "@styles/palette-vars";
import { useMarkupsViewQueryParams } from "@pages/project-details/project-markups/calendar/use-markups-view-query-params";
import { useCallback, useEffect, useRef, useState } from "react";
/**
 * Component representing a side panel for displaying details of a selected markup.
 * It allows users to view information about a specific markup within a project.
 */
export function MarkupSidePanel(t0) {
  const $ = _c(57);
  const {
    layout,
    markups
  } = t0;
  const {
    isScreenXs
  } = useMediaQueryList();
  const {
    isOpenMarkupSidePanel,
    selectedMarkup,
    isMarkupUpdateInProgress,
    activeTab
  } = useMarkupContext();
  const t1 = selectedMarkup?.id ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = incompleteMarkupAttachmentUploadTasksSelector(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = useAppSelector(t2);
  let t4;
  if ($[2] !== t3) {
    t4 = t3.some(isTaskInProgress);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isUploadingAttachments = t4;
  const selectedIndex = markups?.findIndex(t5 => {
    const {
      id
    } = t5;
    return id === selectedMarkup?.id;
  }) ?? -1;
  const nextMarkupId = selectedIndex < 0 ? undefined : markups?.[selectedIndex + 1]?.id;
  let t6;
  if ($[4] !== nextMarkupId) {
    t6 = {
      nextMarkupId
    };
    $[4] = nextMarkupId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const {
    isLoading: isMarkupLoading,
    isNextMarkupLoaded,
    closeSidePanel
  } = useMarkupSidePanel(t6);
  const {
    viewMode
  } = useMarkupsViewQueryParams();
  const shownViewModeRef = useRef(viewMode);
  let t7;
  let t8;
  if ($[6] !== closeSidePanel || $[7] !== viewMode) {
    t7 = () => {
      if (shownViewModeRef.current !== viewMode) {
        shownViewModeRef.current = viewMode;
        closeSidePanel();
      }
    };
    t8 = [viewMode, closeSidePanel];
    $[6] = closeSidePanel;
    $[7] = viewMode;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  const t9 = selectedMarkup?.id;
  const t10 = !isMarkupLoading;
  const t11 = isNextMarkupLoaded ? nextMarkupId : undefined;
  let t12;
  if ($[10] !== layout.isDocked || $[11] !== t10 || $[12] !== t11 || $[13] !== t9) {
    t12 = {
      markupId: t9,
      isMarkupLoaded: t10,
      isEnabled: layout.isDocked,
      nextMarkupId: t11
    };
    $[10] = layout.isDocked;
    $[11] = t10;
    $[12] = t11;
    $[13] = t9;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const previewState = useMarkupPreview(t12);
  const isLoading = isMarkupLoading || previewState.status === "finding";
  let t13;
  if ($[15] !== previewState) {
    t13 = getMarkupPreviewPlaceId(previewState);
    $[15] = previewState;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const placeId = t13;
  let t14;
  if ($[17] !== placeId) {
    t14 = state => {
      const place = placeId ? selectIElement(placeId)(state) : undefined;
      return !!place && (isIElementPointCloudStream(place) || isScanSection(selectIElement(place.parentId ?? undefined)(state)));
    };
    $[17] = placeId;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const isInScan = useAppSelector(t14);
  const [previewLink, setPreviewLink] = useState();
  const markupId = selectedMarkup?.id;
  const sphereViewerUrl = selectedMarkup?.sphereViewerUrl;
  let t15;
  if ($[19] !== markupId || $[20] !== previewState || $[21] !== sphereViewerUrl) {
    t15 = camera => {
      if (!sphereViewerUrl || !markupId || previewState.status !== "ready") {
        return;
      }
      const {
        content
      } = previewState;
      const id_0 = content.kind === "panorama" ? content.panorama.id : content.pointCloud.id;
      const url = new URL(sphereViewerUrl);
      url.searchParams.set("id", id_0);
      url.searchParams.set("lookAtId", markupId);
      url.searchParams.set("mode", "walk");
      url.searchParams.set("scene", content.kind === "panorama" ? "360" : "pc");
      url.searchParams.set("camera", camera);
      setPreviewLink({
        markupId,
        href: url.toString()
      });
    };
    $[19] = markupId;
    $[20] = previewState;
    $[21] = sphereViewerUrl;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const onCameraChange = t15;
  const viewerHref = layout.isDocked && previewState.status === "ready" && previewLink?.markupId === markupId ? previewLink?.href : undefined;
  const t16 = layout.isDocked ? "persistent" : "temporary";
  const t17 = isScreenXs ? "100%" : `${layout.width}px`;
  let t18;
  if ($[23] !== t17) {
    t18 = {
      "& .MuiDrawer-paper": {
        boxShadow: 8,
        borderLeft: `1px solid ${PALETTE_VARS.divider}`,
        width: t17,
        paddingX: 3,
        paddingBottom: 2,
        overflow: "hidden"
      }
    };
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      height: "100%",
      gap: 1,
      pt: 2
    };
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== layout) {
    t20 = layout.isDocked && <MarkupSidePanelResizeHandle layout={layout} />;
    $[26] = layout;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== closeSidePanel || $[29] !== isLoading || $[30] !== markups || $[31] !== placeId || $[32] !== selectedMarkup || $[33] !== viewerHref) {
    t21 = selectedMarkup && <MarkupsSidePanelHeader markup={selectedMarkup} markups={markups} closeSidePanel={closeSidePanel} viewerHref={viewerHref} isLoading={isLoading} placeId={placeId} />;
    $[28] = closeSidePanel;
    $[29] = isLoading;
    $[30] = markups;
    $[31] = placeId;
    $[32] = selectedMarkup;
    $[33] = viewerHref;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      flexGrow: 1,
      minHeight: 0,
      overflowY: "auto"
    };
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== activeTab || $[37] !== isInScan || $[38] !== isLoading || $[39] !== isMarkupUpdateInProgress || $[40] !== isUploadingAttachments || $[41] !== layout.isDocked || $[42] !== layout.isWide || $[43] !== onCameraChange || $[44] !== previewState || $[45] !== selectedMarkup) {
    t23 = <Box sx={t22}>{isLoading ? <Skeleton variant="rectangular" sx={{
        width: "100%",
        height: "100%"
      }} /> : <StickySidePanelContainer selectedTab={activeTab} tabs={[{
        label: "Info",
        isDisabled: isMarkupUpdateInProgress(),
        content: () => selectedMarkup && <MarkupsSidePanelInfo markup={selectedMarkup} isWide={layout.isWide} isInScan={isInScan} preview={layout.isDocked && <MarkupPreview markupId={selectedMarkup.id} previewState={previewState} onCameraChange={onCameraChange} />} />
      }, {
        label: "Attachments",
        count: selectedMarkup?.attachmentsCount,
        isBusy: isUploadingAttachments,
        isDisabled: isMarkupUpdateInProgress(),
        content: () => selectedMarkup && <MarkupsSidePanelAttachments markup={selectedMarkup} />
      }]} />}</Box>;
    $[36] = activeTab;
    $[37] = isInScan;
    $[38] = isLoading;
    $[39] = isMarkupUpdateInProgress;
    $[40] = isUploadingAttachments;
    $[41] = layout.isDocked;
    $[42] = layout.isWide;
    $[43] = onCameraChange;
    $[44] = previewState;
    $[45] = selectedMarkup;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t20 || $[48] !== t21 || $[49] !== t23) {
    t24 = <Stack sx={t19}>{t20}{t21}{t23}</Stack>;
    $[47] = t20;
    $[48] = t21;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== closeSidePanel || $[52] !== isOpenMarkupSidePanel || $[53] !== t16 || $[54] !== t18 || $[55] !== t24) {
    t25 = <SidePanel isOpen={isOpenMarkupSidePanel} anchor="right" closeSidePanel={closeSidePanel} isBackdropInvisible={true} variant={t16} sx={t18}>{t24}</SidePanel>;
    $[51] = closeSidePanel;
    $[52] = isOpenMarkupSidePanel;
    $[53] = t16;
    $[54] = t18;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  return t25;
}